import { BlueprintFormData } from '../types';
import { RentalSource } from './constants';
import { getBlueprintCalculations } from 'features/blueprints';
import { powerProfileModes } from 'greener-ui-library';
import { projectTypeIdToString } from 'pages/Blueprints/components/utils';
import { Blueprint } from 'types';
import { convertFromCents } from 'utils/currencyUtils';

export const mapBlueprintToFormData = (blueprint: Blueprint): Partial<BlueprintFormData> => {
  const { hybrid, conventional } = getBlueprintCalculations(blueprint);

  if (!hybrid || !conventional) {
    return {};
  }

  return {
    blueprintName: blueprint.name,
    clientName: blueprint.organisationName,
    applicationType: projectTypeIdToString(blueprint.projectTypeId),
    startDate: new Date(blueprint.tsStart),
    endDate: blueprint.tsEnd ? new Date(blueprint.tsEnd) : undefined,

    // Determine mode from powerProfileCreateMode from API, default to manual if not present
    powerProfileMode: blueprint.powerProfileCreateMode === 'file' ? powerProfileModes.file : powerProfileModes.manual,
    peakPowerKw: hybrid.loadProfile?.peakLoadKw,
    averagePowerKw: undefined,
    workingDays:
      hybrid.loadProfile?.days?.map((d) => {
        const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
        return dayNames[d.dayOfWeek];
      }) || [],
    workingHoursStart: hybrid.loadProfile?.startHour
      ? `${hybrid.loadProfile.startHour.toString().padStart(2, '0')}:00`
      : '08:00',
    workingHoursEnd: hybrid.loadProfile?.endHour
      ? `${hybrid.loadProfile.endHour.toString().padStart(2, '0')}:00`
      : '15:00',
    // Step 2 values come from conventional calculation
    peaksPerDay: undefined,
    peaksDuration: undefined,
    restDayAveragePowerKw: undefined,
    gensetKva: conventional.powerSetup.generatorCapacityKva || '',
    gensetCount: conventional.powerSetup.generatorCount || '',
    mainsConnection: (hybrid.powerSetup.mainsConnectionMaxAmps ?? 0) > 0,
    maxAmpsAvailable: hybrid.powerSetup.mainsConnectionMaxAmps ?? 0,

    // Business Case
    currency: hybrid.currencyCode.toUpperCase(),
    gensetRentalSource: hybrid.powerSetup.generatorTypeId != null ? RentalSource.CATALOG : RentalSource.MANUAL,
    gensetType: hybrid.powerSetup.generatorTypeId != null ? hybrid.powerSetup.generatorTypeId.toString() : '',
    // Only set hybridGensetKva if rental source is MANUAL (no generatorTypeId)
    hybridGensetKva:
      hybrid.powerSetup.generatorTypeId == null ? (hybrid.powerSetup.generatorCapacityKva ?? undefined) : undefined,
    gensetRentalCount: hybrid.powerSetup.generatorCount!,
    gensetRentalCost: convertFromCents(hybrid.costAssumptions.gensetRentalCostCents),

    batteryRentalSource: hybrid.powerSetup.batteryTypeId != null ? RentalSource.CATALOG : RentalSource.MANUAL,
    batteryModel: hybrid.powerSetup.batteryTypeId != null ? hybrid.powerSetup.batteryTypeId.toString() : '',
    // Only set batterySize and converterPower if rental source is MANUAL (no batteryTypeId)
    batterySize: hybrid.powerSetup.batteryTypeId == null ? (hybrid.powerSetup.batterySizeKwh ?? undefined) : undefined,
    converterPower:
      hybrid.powerSetup.batteryTypeId == null ? (hybrid.powerSetup.batteryConverterPowerKw ?? undefined) : undefined,
    batteryRentalCount: hybrid.powerSetup.batteryCount || 1,
    batteryRentalCost: convertFromCents(hybrid.costAssumptions.batteryRentalCostCents || 0),

    hybridDieselPricePerLiter: convertFromCents(hybrid.costAssumptions.dieselPricePerLiterPriceCents),
    hybridElectricityPricePerKwh: hybrid.costAssumptions.energyPricePerKwhPriceCents
      ? convertFromCents(hybrid.costAssumptions.energyPricePerKwhPriceCents)
      : undefined,
    hybridAdditionalCostsPerWeek: hybrid.costAssumptions.additionalCostsCents
      ? convertFromCents(hybrid.costAssumptions.additionalCostsCents)
      : undefined,

    conventionalDieselPricePerLiter: convertFromCents(conventional.costAssumptions.dieselPricePerLiterPriceCents),
    conventionalGensetRentalCostPerWeek: convertFromCents(conventional.costAssumptions.gensetRentalCostCents),
    conventionalAdditionalCostsPerWeek: conventional.costAssumptions.additionalCostsCents
      ? convertFromCents(conventional.costAssumptions.additionalCostsCents)
      : undefined,
  };
};
